import { createAsyncThunk } from '@reduxjs/toolkit'

import { toast } from '@buffer-mono/popcorn'

import type { NewIdea } from '~publish/components/LegacyIdeaComposer/IdeaComposer'
import { useComposerDialogStore } from '~publish/hooks/composerDialogStore'
import { client } from '~publish/legacy/apollo-client'
import { graphql } from '~publish/graphql'
import { selectProfiles } from '~publish/legacy/profile-sidebar/selectors'
import type { AppDispatch, RootState } from '~publish/legacy/store'
import { isExtendedMedia } from '~publish/pages/Create/helpers'
import { isService } from '~publish/helpers/channels'
import {
  parseIdeaToComposerPost,
  getFirstVideo,
  getFirstDocument,
} from '~publish/pages/Create/helpers/parsers'
import type {
  ExtendedIdea,
  ExtendedMedia,
  Idea,
} from '~publish/pages/Create/types'

export const GET_UPLOAD = graphql(/* GraphQL */ `
  query upload($input: UploadIdInput!) {
    upload(input: $input) {
      ... on UploadDetails {
        id
        type
        height
        width
        duration
        durationMs
        location
        transcodedLocation
      }
      ... on CoreWebAppCommonError {
        success
        message
      }
    }
  }
`)

export const createPostFromIdea = createAsyncThunk<
  void,
  { idea: Idea | NewIdea; user: { id: string; email: string } },
  { state: RootState; dispatch: AppDispatch }
>('ideas/createPostFromIdea', async ({ idea, user }, { getState }) => {
  try {
    const mediaToParse = idea?.content?.media || []
    const firstVideo = getFirstVideo(mediaToParse)

    let extendedVideo: ExtendedMedia | undefined

    if (firstVideo) {
      const uploadId = firstVideo.id
      if (!uploadId) return

      const response = await client.query({
        query: GET_UPLOAD,
        variables: { input: { id: uploadId } },
      })

      const upload = response.data?.upload
      // `upload` is a union — an error response (`CoreWebAppCommonError`) must
      // not be read as media, so narrow on `__typename` before destructuring.
      if (upload?.__typename === 'UploadDetails') {
        const {
          height,
          width,
          duration,
          durationMs,
          location,
          transcodedLocation,
        } = upload
        const enrichedVideo = {
          ...firstVideo,
          height,
          width,
          duration,
          durationMs,
          location: location ?? undefined,
          transcodedLocation: transcodedLocation ?? undefined,
        }
        if (isExtendedMedia(enrichedVideo)) {
          extendedVideo = enrichedVideo
        }
      }
    }

    const firstDocument = getFirstDocument(mediaToParse)
    let updatedMedia = mediaToParse

    if (extendedVideo && isExtendedMedia(extendedVideo)) {
      updatedMedia = mediaToParse.map((media) =>
        media?.id === firstVideo?.id ? extendedVideo : media,
      )
    } else if (firstDocument) {
      updatedMedia = mediaToParse.map((media) =>
        media?.id === firstDocument?.id ? firstDocument : media,
      )
    }

    const profiles = selectProfiles(getState())
    const services = idea.content.services ?? []

    const profileIds = profiles
      .filter(
        (profile) =>
          isService(profile.service) && services.includes(profile.service),
      )
      .map((profile) => profile.id)

    const ideaForComposer: ExtendedIdea = {
      ...idea,
      id: idea.id ?? '',
      content: { ...idea.content, media: updatedMedia },
    }
    const payload = {
      selectedProfileIds: profileIds,
      prefillPostData: parseIdeaToComposerPost(ideaForComposer, user),
      idea: ideaForComposer,
    }

    useComposerDialogStore.getState().handleCreatePostClick(payload)
  } catch {
    toast.error(
      `Sorry! Something went wrong while creating your post. Would you be up for trying again?`,
    )
  }
})
